/**
 * A tiny, dependency-free celebration for a child completing work.
 * It honours reduced-motion preferences and cleans up every generated node.
 */
export const CHILD_CONFETTI_DURATION_MS = 2_200;
export const GOAL_STAR_DURATION_MS = 2_800;

/**
 * The action can re-render while its request is in flight.  Capture its
 * bounding rectangle at click time and pass that snapshot here, rather than
 * measuring a detached/replaced button after the request succeeds.
 */
export type CelebrationOrigin = HTMLElement | DOMRectReadOnly;

function originRect(origin?: CelebrationOrigin): DOMRectReadOnly | undefined {
  if (origin instanceof HTMLElement) return origin.getBoundingClientRect();
  return origin;
}

function celebrationStart(origin?: CelebrationOrigin): { x: number; y: number } {
  const source =
    origin ?? (document.activeElement instanceof HTMLElement ? document.activeElement : undefined);
  const rect = originRect(source);
  return {
    x: rect ? rect.left + rect.width / 2 : window.innerWidth / 2,
    y: rect ? rect.top + rect.height / 2 : window.innerHeight / 2,
  };
}

export function burstChildConfetti(origin?: CelebrationOrigin): void {
  if (
    typeof document === 'undefined' ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }
  const { x, y } = celebrationStart(origin);
  const colours = ['#f58b72', '#3193df', '#f2bd38', '#48a978', '#8c62c9'];

  for (let index = 0; index < 18; index += 1) {
    const piece = document.createElement('i');
    const angle = (Math.PI * 2 * index) / 18;
    const distance = 44 + (index % 4) * 13;
    piece.className = 'child-confetti-piece';
    piece.style.setProperty('--confetti-x', `${Math.cos(angle) * distance}px`);
    piece.style.setProperty('--confetti-y', `${Math.sin(angle) * distance + 60}px`);
    piece.style.setProperty('--confetti-colour', colours[index % colours.length] ?? '#3193df');
    piece.style.left = `${x}px`;
    piece.style.top = `${y}px`;
    document.body.append(piece);
    window.setTimeout(() => piece.remove(), CHILD_CONFETTI_DURATION_MS + 100);
  }
}

export function burstGoalStars(origin?: CelebrationOrigin): void {
  if (
    typeof document === 'undefined' ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
    return;
  const { x, y } = celebrationStart(origin);
  for (let index = 0; index < 14; index += 1) {
    const star = document.createElement('span');
    const angle = (Math.PI * 2 * index) / 14;
    star.className = 'child-goal-star';
    star.textContent = index % 3 === 0 ? '✨' : '⭐';
    star.style.setProperty('--star-x', `${Math.cos(angle) * (72 + (index % 4) * 18)}px`);
    star.style.setProperty('--star-y', `${Math.sin(angle) * (72 + (index % 4) * 18)}px`);
    star.style.setProperty('--star-delay', `${(index % 3) * 35}ms`);
    star.style.left = `${x}px`;
    star.style.top = `${y}px`;
    document.body.append(star);
    window.setTimeout(() => star.remove(), GOAL_STAR_DURATION_MS + 200);
  }
}
